'use client';
import React, { useState, useRef, useCallback } from 'react';

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

export default function ExtractAudioTool() {
  const [videoFile, setVideoFile] = useState<{ file: File; url: string; size: number } | null>(null);
  const [outputFormat, setOutputFormat] = useState<'mp3' | 'wav' | 'aac'>('mp3');
  const [processing, setProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressLabel, setProgressLabel] = useState('');
  const [resultUrl, setResultUrl] = useState<string | null>(null);
  const [resultSize, setResultSize] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadFile = useCallback((file: File) => {
    if (file.size > 1024 * 1024 * 1024) { setError('File too large. Max 1 GB.'); return; }
    setError(null); setResultUrl(null);
    setVideoFile({ file, url: URL.createObjectURL(file), size: file.size });
  }, []);

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault(); setIsDragging(false);
    const f = e.dataTransfer.files?.[0]; if (f) loadFile(f);
  };

  const handleExtract = async () => {
    if (!videoFile) return;
    setProcessing(true); setProgress(0); setError(null); setResultUrl(null);
    try {
      setProgressLabel('Loading FFmpeg engine…'); setProgress(10);
      const { FFmpeg } = await import('@ffmpeg/ffmpeg');
      const { fetchFile, toBlobURL } = await import('@ffmpeg/util');
      const ffmpeg = new FFmpeg();
      ffmpeg.on('progress', ({ progress: p }) => { setProgress(Math.round(15 + p * 75)); setProgressLabel(`Extracting audio… ${Math.round(p * 100)}%`); });
      const baseURL = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd';
      await ffmpeg.load({
        coreURL: await toBlobURL(`${baseURL}/ffmpeg-core.js`, 'text/javascript'),
        wasmURL: await toBlobURL(`${baseURL}/ffmpeg-core.wasm`, 'application/wasm'),
      });
      setProgress(15); setProgressLabel('Reading video file…');
      const ext = videoFile.file.name.split('.').pop() || 'mp4';
      await ffmpeg.writeFile(`input.${ext}`, await fetchFile(videoFile.file));

      const args = ['-i', `input.${ext}`, '-vn'];
      if (outputFormat === 'mp3') args.push('-codec:a', 'libmp3lame', '-b:a', '192k');
      else if (outputFormat === 'aac') args.push('-codec:a', 'aac', '-b:a', '192k');
      else args.push('-codec:a', 'pcm_s16le');
      args.push(`output.${outputFormat}`);

      await ffmpeg.exec(args);
      setProgress(95); setProgressLabel('Preparing download…');
      const data = await ffmpeg.readFile(`output.${outputFormat}`);
      const mimeMap: Record<string, string> = { mp3: 'audio/mpeg', wav: 'audio/wav', aac: 'audio/aac' };
      const blob = new Blob([data], { type: mimeMap[outputFormat] });
      setResultUrl(URL.createObjectURL(blob));
      setResultSize(blob.size);
      setProgress(100); setProgressLabel('Done!');
    } catch (err) {
      console.error(err);
      setError('Extraction failed. Make sure the video file contains an audio track.');
    } finally {
      setProcessing(false);
    }
  };

  const handleDownload = () => {
    if (!resultUrl || !videoFile) return;
    const a = document.createElement('a');
    a.href = resultUrl;
    a.download = `${videoFile.file.name.replace(/\.[^.]+$/, '')}_audio.${outputFormat}`;
    a.click();
  };

  return (
    <div className="space-y-5">
      {error && (
        <div className="flex items-start gap-3 bg-red-50 border border-red-100 rounded-xl p-4">
          <svg className="w-5 h-5 text-red-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
          <p className="text-red-700 text-sm">{error}</p>
        </div>
      )}
      {!videoFile ? (
        <div
          className={`rounded-2xl border-2 border-dashed p-10 text-center cursor-pointer transition-colors ${isDragging ? 'border-amber-400 bg-amber-50' : 'border-gray-200 bg-gray-50 hover:border-amber-300 hover:bg-amber-50/50'}`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
        >
          <span className="text-4xl block mb-3">🎬</span>
          <p className="font-semibold text-gray-800 mb-1">Upload Video File</p>
          <p className="text-gray-500 text-sm mb-3">Drag & drop or click to browse</p>
          <p className="text-gray-400 text-xs">Supports MP4, MKV, AVI, MOV, WebM, FLV</p>
          <input ref={fileInputRef} type="file" accept=".mp4,.mkv,.avi,.mov,.webm,.flv,.wmv,.m4v" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) loadFile(f); }} />
        </div>
      ) : (
        <div className="space-y-4">
          <div className="flex items-center gap-3 bg-amber-50 border border-amber-100 rounded-xl p-4">
            <span className="text-2xl">🎬</span>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-gray-800 text-sm truncate">{videoFile.file.name}</p>
              <p className="text-gray-500 text-xs">{formatBytes(videoFile.size)}</p>
            </div>
            <button onClick={() => { setVideoFile(null); setResultUrl(null); }} className="text-gray-400 hover:text-red-500 p-1">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
          </div>
          {videoFile.size > 100 * 1024 * 1024 && (
            <div className="flex items-start gap-2 bg-yellow-50 border border-yellow-100 rounded-xl p-3">
              <span className="text-yellow-500 text-sm">⚠️</span>
              <p className="text-yellow-700 text-xs">Large video files may take longer because processing happens in your browser.</p>
            </div>
          )}
          <div>
            <p className="text-sm font-semibold text-gray-700 mb-2">Output Audio Format</p>
            <div className="flex gap-3">
              {(['mp3', 'wav', 'aac'] as const).map(fmt => (
                <button key={fmt} onClick={() => setOutputFormat(fmt)} className={`px-5 py-2 rounded-xl text-sm font-semibold border transition-colors ${outputFormat === fmt ? 'bg-amber-500 text-white border-amber-500' : 'bg-white text-gray-700 border-gray-200 hover:border-amber-300'}`}>{fmt.toUpperCase()}</button>
              ))}
            </div>
          </div>
          {processing && (
            <div>
              <div className="flex justify-between text-xs text-gray-500 mb-1"><span>{progressLabel}</span><span>{progress}%</span></div>
              <div className="h-2 bg-gray-100 rounded-full overflow-hidden"><div className="h-full bg-gradient-to-r from-amber-400 to-amber-600 rounded-full transition-all duration-300" style={{ width: `${progress}%` }} /></div>
            </div>
          )}
          {resultUrl && (
            <div className="bg-green-50 border border-green-100 rounded-xl p-4 space-y-3">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                <p className="font-semibold text-green-800 text-sm">Audio extracted! · {formatBytes(resultSize)}</p>
              </div>
              <audio src={resultUrl} controls className="w-full h-10" />
              <button onClick={handleDownload} className="w-full py-3 bg-green-600 hover:bg-green-700 text-white font-semibold rounded-xl transition-colors text-sm">⬇️ Download {outputFormat.toUpperCase()} Audio</button>
            </div>
          )}
          {!resultUrl && (
            <button onClick={handleExtract} disabled={processing} className="w-full py-3.5 bg-amber-500 hover:bg-amber-600 disabled:opacity-60 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-colors text-sm">
              {processing ? 'Extracting…' : 'Extract Audio'}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
